import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { useAppSelector } from 'store/hooks'
import { isAppInitializedSelector, sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import type { SupportedCurrencies } from 'types/currencies'
import { getDataFromSessionStorage, INTERNAL_GAME_NAVIGATION, setDataToSessionStorage } from 'utils/sessionStorage'

// We have url path like:
// Game page /slots/christmas-reach-bonus-buy/play and /slots/christmas-reach-bonus-buy/sweepstake/play
// Game info page /slots/christmas-reach-bonus-buy and /slots/christmas-reach-bonus-buy/sweepstake
// Bingo room page (GC) /bingo/rooms/club-75-gc/play and (SC) /bingo/rooms/club-75/sweepstake/play
// Bingo page /bingo/play and /bingo/sweepstake/play

const getUrlWithSearchParams = (
  url: string,
  isInternalGameNavigation: boolean,
  searchParams?: string,
  isCurrencySwitched?: boolean,
) => {
  const isSearchParamsWithUtm = !!searchParams?.includes('utm_source')

  if (isInternalGameNavigation || isSearchParamsWithUtm || isCurrencySwitched) {
    return `${url}?${searchParams}`
  } else {
    return `${url}?refererPath=directUrl`
  }
}

const getRedirectUrl = (
  path: string,
  routerKey: string,
  activeCurrency: SupportedCurrencies,
  sweepstakeEnabled: boolean,
) => {
  const isBingoRoom = path.includes('/bingo/rooms/')

  if (!sweepstakeEnabled && path.includes('sweepstake')) {
    let newPath = path.replace(`${routerKey}/sweepstake`, `${routerKey}`)

    // Handle bingo room ID transformation: add -gc suffix when removing sweepstake
    if (isBingoRoom && !routerKey.endsWith('-gc')) {
      newPath = newPath.replace(`/rooms/${routerKey}`, `/rooms/${routerKey}-gc`)
    }

    return newPath
  }

  if (activeCurrency === 'SC' && !path.includes('sweepstake')) {
    let newPath = path.replace(`${routerKey}`, `${routerKey}/sweepstake`).replace('gc_', 'sc_')

    // Handle bingo room ID transformation: remove -gc suffix when adding sweepstake
    if (isBingoRoom && routerKey.endsWith('-gc')) {
      const roomIdWithoutGc = routerKey.replace(/-gc$/, '')
      newPath = newPath.replace(`/rooms/${routerKey}`, `/rooms/${roomIdWithoutGc}`)
    }

    return newPath
  }

  if (activeCurrency === 'GC' && path.includes('sweepstake')) {
    let newPath = path.replace(`${routerKey}/sweepstake`, `${routerKey}`).replace('sc_', 'gc_')

    // Handle bingo room ID transformation: add -gc suffix when removing sweepstake
    if (isBingoRoom && !routerKey.endsWith('-gc')) {
      newPath = newPath.replace(`/rooms/${routerKey}`, `/rooms/${routerKey}-gc`)
    }

    return newPath
  }

  return path
}

interface UseCurrencyRedirectProps {
  shouldCheckDirectLink: boolean
}

export default function useCurrencyRedirect({ shouldCheckDirectLink }: UseCurrencyRedirectProps): null {
  const router = useRouter()
  const pathname = usePathname()
  const params = useParams()
  const searchParams = useSearchParams()
  const activeCurrency = useSelector(getActiveCurrencyIdSelector)
  const sweepstakeEnabled = useAppSelector(sweepstakeEnabledSelector)
  const isAppInitialized = useAppSelector(isAppInitializedSelector)
  const routerKey = (params.roomId || params.gameId || 'bingo').toString()
  const searchParamsString = searchParams.toString()
  const internalGameNavigationFlag = getDataFromSessionStorage(INTERNAL_GAME_NAVIGATION) === 'true'
  const isCurrencySwitched =
    (activeCurrency === 'SC' && !pathname.includes('sweepstake')) ||
    (activeCurrency === 'GC' && pathname.includes('sweepstake'))

  useEffect(() => {
    if (!isAppInitialized) {
      return undefined
    }

    if (isCurrencySwitched && sweepstakeEnabled) {
      setDataToSessionStorage(INTERNAL_GAME_NAVIGATION, 'true')
    }

    const currencyRedirectUrl = getRedirectUrl(pathname, routerKey, activeCurrency, sweepstakeEnabled)
    const redirectUrlWithSearchParams = getUrlWithSearchParams(
      currencyRedirectUrl,
      internalGameNavigationFlag,
      searchParamsString,
      isCurrencySwitched,
    )
    const redirectUrl = shouldCheckDirectLink ? redirectUrlWithSearchParams : currencyRedirectUrl

    if (currencyRedirectUrl !== pathname || !internalGameNavigationFlag) {
      router.replace(redirectUrl)
    }
  }, [
    isAppInitialized,
    sweepstakeEnabled,
    activeCurrency,
    routerKey,
    pathname,
    router,
    searchParamsString,
    internalGameNavigationFlag,
    shouldCheckDirectLink,
    isCurrencySwitched,
  ])

  return null
}
